import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Calendar, Tag, FileText, 
  CreditCard, LogOut, Menu, X, ChevronRight, DollarSign, Users, 
  TrendingUp, Plus, Edit, Trash2, Check, AlertCircle, Eye, ClipboardList
} from 'lucide-react';

const MAIN_LOGO = "https://customer-assets.emergentagent.com/job_goalgeous-hub/artifacts/ad5dhro0_Untitled%20design.png";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth helper
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
});

// Sidebar navigation items
const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'products', label: 'Products', icon: Package },
  { id: 'orders', label: 'Orders', icon: ShoppingCart },
  { id: 'bookings', label: 'Bookings', icon: Calendar },
  { id: 'discounts', label: 'Discount Codes', icon: Tag },
  { id: 'transactions', label: 'Transactions', icon: CreditCard },
  { id: 'content', label: 'Page Content', icon: FileText },
  { id: 'forms', label: 'Form Submissions', icon: ClipboardList },
];

export const AdminDashboardPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, productsRes, ordersRes, bookingsRes, discountsRes, transactionsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/dashboard/stats`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/products`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/orders`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/bookings`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/discount-codes`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/transactions`, { headers: getAuthHeaders() }),
      ]);

      if (!statsRes.ok) throw new Error('Unauthorized');

      setStats(await statsRes.json());
      setProducts(await productsRes.json());
      setOrders(await ordersRes.json());
      setBookings(await bookingsRes.json());
      setDiscountCodes(await discountsRes.json());
      setTransactions(await transactionsRes.json());
    } catch (err) {
      console.error(err);
      if (err.message === 'Unauthorized') {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    await fetch(`${API_URL}/api/admin/products/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    fetchData();
  };

  const updateBookingStatus = async (id, status) => {
    await fetch(`${API_URL}/api/admin/bookings/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status })
    });
    fetchData();
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    await fetch(`${API_URL}/api/admin/bookings/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    fetchData();
  };

  const toggleDiscountCode = async (id, active) => {
    await fetch(`${API_URL}/api/admin/discount-codes/${id}?active=${!active}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    fetchData();
  };

  const deleteDiscountCode = async (id) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) return;
    await fetch(`${API_URL}/api/admin/discount-codes/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    fetchData();
  };

  // Product Form Component
  const ProductForm = ({ product, onClose }) => {
    const [formData, setFormData] = useState(product || {
      name: '', description: '', price: '', category: 'apparel',
      subcategory: '', brand: 'power_up', image_url: '', inventory: 0,
      is_digital: false, digital_file_url: '', active: true
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      const method = product ? 'PUT' : 'POST';
      const url = product 
        ? `${API_URL}/api/admin/products/${product.id}`
        : `${API_URL}/api/admin/products`;
      
      await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...formData, price: parseFloat(formData.price) })
      });
      
      onClose();
      fetchData();
    };

    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-brand-dark border border-white/10 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <h3 className="font-oswald text-xl uppercase text-white mb-6">
            {product ? 'Edit Product' : 'Add New Product'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-brand-gray mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-brand-gray mb-1">Price</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-brand-gray mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white h-20"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-brand-gray mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white"
                >
                  <option value="apparel">Apparel</option>
                  <option value="digital">Digital Product</option>
                  <option value="service">Service</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-brand-gray mb-1">Brand</label>
                <select
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white"
                >
                  <option value="power_up">Power Up Your Fitness</option>
                  <option value="goalgeous">GOALGEOUS</option>
                  <option value="club_deuces">Club Deuces</option>
                  <option value="2000watts">2000 WATTS</option>
                  <option value="goaltrition">GOAL-Trition</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-brand-gray mb-1">Subcategory</label>
                <input
                  type="text"
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  placeholder="e.g., hoodie, tshirt, hat"
                  className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-brand-gray mb-1">Inventory</label>
                <input
                  type="number"
                  value={formData.inventory}
                  onChange={(e) => setFormData({ ...formData, inventory: parseInt(e.target.value) })}
                  className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-brand-gray mb-1">Image URL</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-white">
                <input
                  type="checkbox"
                  checked={formData.is_digital}
                  onChange={(e) => setFormData({ ...formData, is_digital: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm">Digital Product</span>
              </label>
              <label className="flex items-center gap-2 text-white">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm">Active</span>
              </label>
            </div>
            {formData.is_digital && (
              <div>
                <label className="block text-sm text-brand-gray mb-1">Digital File URL</label>
                <input
                  type="url"
                  value={formData.digital_file_url}
                  onChange={(e) => setFormData({ ...formData, digital_file_url: e.target.value })}
                  className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white"
                />
              </div>
            )}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                className="flex-1 bg-brand-lime text-brand-black font-oswald uppercase py-3 hover:bg-brand-pink hover:text-white transition-colors"
              >
                {product ? 'Update Product' : 'Create Product'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 border border-white/20 text-white font-oswald uppercase py-3 hover:bg-white/10"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Discount Code Form Component
  const DiscountForm = ({ onClose }) => {
    const [formData, setFormData] = useState({
      code: '', discount_type: 'percentage', discount_value: '',
      min_purchase: 0, max_uses: '', active: true
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      await fetch(`${API_URL}/api/admin/discount-codes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          discount_value: parseFloat(formData.discount_value),
          max_uses: formData.max_uses ? parseInt(formData.max_uses) : null
        })
      });
      onClose();
      fetchData();
    };

    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-brand-dark border border-white/10 p-6 max-w-md w-full">
          <h3 className="font-oswald text-xl uppercase text-white mb-6">Create Discount Code</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-brand-gray mb-1">Code</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white uppercase"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-brand-gray mb-1">Type</label>
                <select
                  value={formData.discount_type}
                  onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                  className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white"
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-brand-gray mb-1">Value</label>
                <input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                  placeholder={formData.discount_type === 'percentage' ? '10' : '20.00'}
                  className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-brand-gray mb-1">Max Uses (optional)</label>
              <input
                type="number"
                value={formData.max_uses}
                onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                className="w-full bg-brand-black border border-white/20 px-3 py-2 text-white"
              />
            </div>
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                className="flex-1 bg-brand-lime text-brand-black font-oswald uppercase py-3 hover:bg-brand-pink hover:text-white transition-colors"
              >
                Create Code
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 border border-white/20 text-white font-oswald uppercase py-3 hover:bg-white/10"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Render content based on active tab
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-brand-lime border-t-transparent rounded-full" />
        </div>
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            <h2 className="font-oswald text-2xl uppercase text-white">Dashboard Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-brand-dark border border-white/10 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-brand-lime/10 flex items-center justify-center">
                    <DollarSign className="text-brand-lime" />
                  </div>
                  <div>
                    <p className="text-brand-gray text-sm">Total Revenue</p>
                    <p className="font-oswald text-2xl text-white">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </div>
              <div className="bg-brand-dark border border-white/10 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-brand-pink/10 flex items-center justify-center">
                    <ShoppingCart className="text-brand-pink" />
                  </div>
                  <div>
                    <p className="text-brand-gray text-sm">Total Orders</p>
                    <p className="font-oswald text-2xl text-white">{stats?.total_orders || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-brand-dark border border-white/10 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-brand-lime/10 flex items-center justify-center">
                    <Calendar className="text-brand-lime" />
                  </div>
                  <div>
                    <p className="text-brand-gray text-sm">Pending Bookings</p>
                    <p className="font-oswald text-2xl text-white">{stats?.pending_bookings || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-brand-dark border border-white/10 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-brand-pink/10 flex items-center justify-center">
                    <Package className="text-brand-pink" />
                  </div>
                  <div>
                    <p className="text-brand-gray text-sm">Total Products</p>
                    <p className="font-oswald text-2xl text-white">{stats?.total_products || 0}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Recent Orders */}
            <div className="bg-brand-dark border border-white/10 p-6">
              <h3 className="font-oswald text-lg uppercase text-white mb-4">Recent Orders</h3>
              {orders.length === 0 ? (
                <p className="text-brand-gray">No orders yet</p>
              ) : (
                <div className="space-y-2">
                  {orders.slice(0, 5).map((order) => (
                    <div key={order.id} className="flex items-center justify-between py-2 border-b border-white/10">
                      <div>
                        <p className="text-white">{order.customer_name}</p>
                        <p className="text-sm text-brand-gray">{order.customer_email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-brand-lime font-oswald">${order.total_amount?.toFixed(2)}</p>
                        <p className="text-xs text-brand-gray">{order.order_id}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'products':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-oswald text-2xl uppercase text-white">Products</h2>
              <button
                onClick={() => { setEditingProduct(null); setShowProductModal(true); }}
                className="flex items-center gap-2 bg-brand-lime text-brand-black px-4 py-2 font-oswald uppercase text-sm hover:bg-brand-pink hover:text-white transition-colors"
              >
                <Plus size={18} /> Add Product
              </button>
            </div>
            <div className="bg-brand-dark border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead className="bg-brand-black">
                  <tr>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Name</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Category</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Brand</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Price</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Status</th>
                    <th className="text-right px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} className="border-t border-white/10">
                      <td className="px-4 py-3 text-white">{product.name}</td>
                      <td className="px-4 py-3 text-brand-gray capitalize">{product.category}</td>
                      <td className="px-4 py-3 text-brand-gray">{product.brand}</td>
                      <td className="px-4 py-3 text-brand-lime">${product.price?.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs ${product.active ? 'bg-brand-lime/20 text-brand-lime' : 'bg-red-500/20 text-red-500'}`}>
                          {product.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => { setEditingProduct(product); setShowProductModal(true); }}
                          className="text-brand-gray hover:text-brand-lime mr-2"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => deleteProduct(product.id)}
                          className="text-brand-gray hover:text-red-500"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {products.length === 0 && (
                <p className="text-center text-brand-gray py-8">No products yet. Add your first product!</p>
              )}
            </div>
          </div>
        );

      case 'orders':
        return (
          <div className="space-y-6">
            <h2 className="font-oswald text-2xl uppercase text-white">Orders</h2>
            <div className="bg-brand-dark border border-white/10 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-brand-black">
                  <tr>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Order ID</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Customer</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Total</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Status</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} className="border-t border-white/10">
                      <td className="px-4 py-3 text-brand-lime font-mono">{order.order_id}</td>
                      <td className="px-4 py-3">
                        <p className="text-white">{order.customer_name}</p>
                        <p className="text-sm text-brand-gray">{order.customer_email}</p>
                      </td>
                      <td className="px-4 py-3 text-white font-oswald">${order.total_amount?.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs ${order.status === 'paid' ? 'bg-brand-lime/20 text-brand-lime' : 'bg-yellow-500/20 text-yellow-500'}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-brand-gray text-sm">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {orders.length === 0 && (
                <p className="text-center text-brand-gray py-8">No orders yet</p>
              )}
            </div>
          </div>
        );

      case 'bookings':
        return (
          <div className="space-y-6">
            <h2 className="font-oswald text-2xl uppercase text-white">Bookings</h2>
            <div className="bg-brand-dark border border-white/10 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-brand-black">
                  <tr>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">ID</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Customer</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Service</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Date/Time</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Status</th>
                    <th className="text-right px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id} className="border-t border-white/10">
                      <td className="px-4 py-3 text-brand-lime font-mono">{booking.booking_id}</td>
                      <td className="px-4 py-3">
                        <p className="text-white">{booking.customer_name}</p>
                        <p className="text-sm text-brand-gray">{booking.customer_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white">{booking.service_name}</p>
                        <p className="text-sm text-brand-gray capitalize">{booking.service_type}</p>
                      </td>
                      <td className="px-4 py-3 text-white">
                        {booking.date} @ {booking.time}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={booking.status}
                          onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                          className={`px-2 py-1 text-xs bg-brand-black border ${
                            booking.status === 'confirmed' ? 'border-brand-lime text-brand-lime' :
                            booking.status === 'cancelled' ? 'border-red-500 text-red-500' :
                            booking.status === 'completed' ? 'border-blue-500 text-blue-500' :
                            'border-yellow-500 text-yellow-500'
                          }`}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="completed">Completed</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => deleteBooking(booking.id)}
                          className="text-brand-gray hover:text-red-500"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bookings.length === 0 && (
                <p className="text-center text-brand-gray py-8">No bookings yet</p>
              )}
            </div>
          </div>
        );

      case 'discounts':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-oswald text-2xl uppercase text-white">Discount Codes</h2>
              <button
                onClick={() => setShowDiscountModal(true)}
                className="flex items-center gap-2 bg-brand-lime text-brand-black px-4 py-2 font-oswald uppercase text-sm hover:bg-brand-pink hover:text-white transition-colors"
              >
                <Plus size={18} /> Add Code
              </button>
            </div>
            <div className="bg-brand-dark border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead className="bg-brand-black">
                  <tr>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Code</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Discount</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Uses</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Status</th>
                    <th className="text-right px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {discountCodes.map((code) => (
                    <tr key={code.id} className="border-t border-white/10">
                      <td className="px-4 py-3 text-brand-lime font-mono font-bold">{code.code}</td>
                      <td className="px-4 py-3 text-white">
                        {code.discount_type === 'percentage' ? `${code.discount_value}%` : `$${code.discount_value}`}
                      </td>
                      <td className="px-4 py-3 text-brand-gray">
                        {code.uses_count} / {code.max_uses || '∞'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleDiscountCode(code.id, code.active)}
                          className={`px-2 py-1 text-xs ${code.active ? 'bg-brand-lime/20 text-brand-lime' : 'bg-red-500/20 text-red-500'}`}
                        >
                          {code.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => deleteDiscountCode(code.id)}
                          className="text-brand-gray hover:text-red-500"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {discountCodes.length === 0 && (
                <p className="text-center text-brand-gray py-8">No discount codes yet</p>
              )}
            </div>
          </div>
        );

      case 'transactions':
        return (
          <div className="space-y-6">
            <h2 className="font-oswald text-2xl uppercase text-white">Payment Transactions</h2>
            <div className="bg-brand-dark border border-white/10 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-brand-black">
                  <tr>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Session ID</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Customer</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Amount</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Status</th>
                    <th className="text-left px-4 py-3 text-brand-gray font-oswald uppercase text-sm">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="border-t border-white/10">
                      <td className="px-4 py-3 text-brand-gray font-mono text-xs">{tx.session_id?.substring(0, 20)}...</td>
                      <td className="px-4 py-3">
                        <p className="text-white">{tx.customer_name}</p>
                        <p className="text-sm text-brand-gray">{tx.customer_email}</p>
                      </td>
                      <td className="px-4 py-3 text-white font-oswald">${tx.total_amount?.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs ${
                          tx.payment_status === 'paid' ? 'bg-brand-lime/20 text-brand-lime' : 'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {tx.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-brand-gray text-sm">
                        {new Date(tx.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {transactions.length === 0 && (
                <p className="text-center text-brand-gray py-8">No transactions yet</p>
              )}
            </div>
          </div>
        );

      case 'content':
        return (
          <div className="space-y-6">
            <h2 className="font-oswald text-2xl uppercase text-white">Page Content Management</h2>
            <p className="text-brand-gray font-montserrat">Edit text content on your website pages without changing the design.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Accountability Chats Page */}
              <div className="bg-brand-dark border border-white/10 p-6 hover:border-brand-lime/30 transition-colors">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-brand-lime/20 flex items-center justify-center">
                    <FileText size={24} className="text-brand-lime" />
                  </div>
                  <div>
                    <h3 className="font-oswald text-lg uppercase text-white">Accountability Chats</h3>
                    <p className="text-sm text-brand-gray">Edit program info, pricing, dates</p>
                  </div>
                </div>
                <Link
                  to="/admin/content/accountability-chats"
                  className="flex items-center gap-2 bg-brand-lime text-brand-black px-4 py-2 font-oswald uppercase text-sm hover:bg-brand-pink hover:text-white transition-colors w-full justify-center"
                >
                  <Edit size={18} /> Edit Content
                </Link>
              </div>
              
              {/* Placeholder for future pages */}
              <div className="bg-brand-dark border border-white/10 p-6 opacity-50">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-brand-gray/20 flex items-center justify-center">
                    <FileText size={24} className="text-brand-gray" />
                  </div>
                  <div>
                    <h3 className="font-oswald text-lg uppercase text-white">More Pages Coming</h3>
                    <p className="text-sm text-brand-gray">Additional pages will be editable soon</p>
                  </div>
                </div>
                <button
                  disabled
                  className="flex items-center gap-2 bg-brand-gray/20 text-brand-gray px-4 py-2 font-oswald uppercase text-sm w-full justify-center cursor-not-allowed"
                >
                  <Edit size={18} /> Coming Soon
                </button>
              </div>
            </div>
          </div>
        );

      case 'forms':
        return (
          <div className="space-y-6">
            <h2 className="font-oswald text-2xl uppercase text-white">Form Submissions</h2>
            <p className="text-brand-gray font-montserrat">Manage intake forms and view submissions.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Choose You Intake Form */}
              <div className="bg-brand-dark border border-white/10 p-6 hover:border-brand-lime/30 transition-colors">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-brand-lime/20 flex items-center justify-center">
                    <ClipboardList size={24} className="text-brand-lime" />
                  </div>
                  <div>
                    <h3 className="font-oswald text-lg uppercase text-white">Choose You Intake</h3>
                    <p className="text-sm text-brand-gray">30-Day Series applications</p>
                  </div>
                </div>
                <Link
                  to="/admin/forms/choose-you-intake"
                  className="flex items-center gap-2 bg-brand-lime text-brand-black px-4 py-2 font-oswald uppercase text-sm hover:bg-brand-pink hover:text-white transition-colors w-full justify-center"
                >
                  <Eye size={18} /> View Submissions
                </Link>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-brand-black flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-brand-dark border-r border-white/10 flex flex-col transition-all duration-300`}>
        <div className="p-4 border-b border-white/10">
          <Link to="/" className="block">
            <img src={MAIN_LOGO} alt="Logo" className={`${sidebarOpen ? 'h-12' : 'h-8'} mx-auto`} />
          </Link>
        </div>
        
        <nav className="flex-1 py-4">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                activeTab === item.id
                  ? 'bg-brand-lime/10 text-brand-lime border-l-2 border-brand-lime'
                  : 'text-brand-gray hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon size={20} />
              {sidebarOpen && <span className="font-montserrat text-sm">{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-brand-gray hover:text-red-500 transition-colors"
          >
            <LogOut size={20} />
            {sidebarOpen && <span className="font-montserrat text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top Bar */}
        <header className="bg-brand-dark border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-brand-gray hover:text-white"
          >
            <Menu size={24} />
          </button>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-brand-gray hover:text-brand-lime text-sm font-montserrat flex items-center gap-1">
              <Eye size={16} /> View Site
            </Link>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {renderContent()}
        </div>
      </main>

      {/* Modals */}
      {showProductModal && (
        <ProductForm
          product={editingProduct}
          onClose={() => { setShowProductModal(false); setEditingProduct(null); }}
        />
      )}
      {showDiscountModal && (
        <DiscountForm onClose={() => setShowDiscountModal(false)} />
      )}
    </div>
  );
};
